// src/components/dubbing/VideoUploadZone.js
import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Paper,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideocamIcon from '@mui/icons-material/Videocam';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { motion, AnimatePresence } from 'framer-motion';
import { COLORS } from '../../utils/color';

const LANGUAGE_BUBBLES = [
  { label: 'ES', color: '#f59e0b' },
  { label: 'FR', color: '#a78bfa' },
  { label: 'DE', color: '#34d399' },
  { label: 'JA', color: '#60a5fa' },
  { label: 'HI', color: '#fb923c' },
  { label: 'AR', color: '#f472b6' },
  { label: 'ZH', color: '#4ade80' },
  { label: 'PT', color: '#fbbf24' },
];

const UploadContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'hasFile' && prop !== 'hasError',
})(({ theme, isDragging, hasFile, hasError }) => ({
  border: `2px dashed ${
    hasError 
      ? COLORS.status.error 
      : isDragging 
        ? COLORS.primary.main 
        : COLORS.border.primary
  }`,
  borderRadius: '16px',
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: hasError
    ? `${COLORS.status.error}05`
    : isDragging 
      ? `${COLORS.primary.main}10` 
      : hasFile 
        ? COLORS.overlay.light 
        : 'transparent',
  cursor: hasFile ? 'default' : 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: hasError 
      ? COLORS.status.error
      : hasFile 
        ? COLORS.border.primary 
        : COLORS.primary.main,
    backgroundColor: hasError
      ? `${COLORS.status.error}10`
      : hasFile 
        ? COLORS.overlay.light 
        : `${COLORS.primary.main}05`,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

const VideoUploadZone = ({
  onFileSelect,
  uploadProgress = 0,
  isUploading = false,
  hasError = false,
  errorMessage = '',
  maxSizeMB = 500,
  acceptedFormats = ['.mp4', '.avi', '.mov', '.mkv', '.webm', '.flv']
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [localError, setLocalError] = useState('');
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    setLocalError('');

    // Basic client-side validation (the main validation is now in parent component)
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!acceptedFormats.includes(fileExtension)) {
      setLocalError(`Invalid file type. Accepted formats: ${acceptedFormats.join(', ')}`);
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      setLocalError(`File too large. Maximum size: ${maxSizeMB}MB`);
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setLocalError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleClick = () => {
    if (!selectedFile && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  // Show error from parent component or local error
  const displayError = errorMessage || localError;
  const showError = hasError || !!localError;

  // Determine if upload was successful
  const uploadComplete = uploadProgress === 100 && !isUploading && selectedFile && !showError;

  return (
    <Box>
      <UploadContainer
        elevation={0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        isDragging={isDragging}
        hasFile={!!selectedFile}
        hasError={showError}
      >
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.join(',')}
          onChange={handleFileInputChange}
          disabled={isUploading}
        />

        {!selectedFile ? (
          // Empty state
          <Box sx={{ position: 'relative' }}>
            {/* Floating language bubbles on drag */}
            <AnimatePresence>
              {isDragging && LANGUAGE_BUBBLES.map((b, i) => (
                <motion.div
                  key={b.label}
                  initial={{ opacity: 0, y: 0, x: (i % 2 === 0 ? -1 : 1) * (20 + (i % 4) * 18) }}
                  animate={{ opacity: [0, 1, 1, 0], y: [-10, -55, -90, -120] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.4, delay: i * 0.09, ease: 'easeOut' }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}
                >
                  <Box sx={{
                    px: 1.4, py: 0.4, borderRadius: '100px',
                    background: `${b.color}25`,
                    border: `1px solid ${b.color}60`,
                    backdropFilter: 'blur(6px)',
                  }}>
                    <Typography sx={{
                      fontSize: '11px', fontWeight: 800, fontFamily: 'Inter, sans-serif',
                      color: b.color, letterSpacing: '0.06em',
                    }}>
                      {b.label}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </AnimatePresence>

            <motion.div
              animate={isDragging ? { scale: 1.15, y: -4 } : { scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 18 }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 64,
                  color: isDragging ? '#a78bfa' : COLORS.text.secondary,
                  mb: 2,
                  transition: 'color 0.3s',
                  filter: isDragging ? 'drop-shadow(0 0 16px #a78bfa80)' : 'none',
                }}
              />
            </motion.div>
            <Typography variant="h6" sx={{ color: COLORS.text.primary, mb: 1 }}>
              {isDragging ? 'Drop your video here' : 'Drag & drop your video here'}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.text.secondary, mb: 2 }}>
              or click to browse
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              {acceptedFormats.map((format) => (
                <Chip
                  key={format}
                  label={format}
                  size="small"
                  sx={{
                    backgroundColor: COLORS.overlay.light,
                    color: COLORS.text.secondary,
                    border: `1px solid ${COLORS.border.primary}`,
                  }}
                />
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: COLORS.text.secondary, mt: 1 }}>
              Maximum file size: {maxSizeMB}MB
            </Typography>
          </Box>
        ) : (
          // File selected state
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              {showError ? (
                <ErrorIcon sx={{ fontSize: 48, color: COLORS.status.error, mr: 2 }} />
              ) : (
                <VideocamIcon sx={{ fontSize: 48, color: COLORS.primary.main, mr: 2 }} />
              )}
              <Box sx={{ textAlign: 'left', flex: 1, maxWidth: '300px' }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: showError ? COLORS.status.error : COLORS.text.primary,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: COLORS.text.secondary }}>
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
              {!isUploading && (
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  sx={{ 
                    ml: 1,
                    color: COLORS.status.error,
                    '&:hover': {
                      backgroundColor: `${COLORS.status.error}10`,
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>

            {isUploading && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: `${COLORS.primary.main}20`,
                    '& .MuiLinearProgress-bar': {
                      background: '#FFFFFF',
                      borderRadius: 4,
                    }
                  }}
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: COLORS.text.secondary, 
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            {uploadComplete && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                <CheckCircleIcon sx={{ color: COLORS.status.success, mr: 1 }} />
                <Typography variant="body2" sx={{ color: COLORS.status.success }}>
                  Upload complete
                </Typography>
              </Box>
            )}

            {showError && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                <ErrorIcon sx={{ color: COLORS.status.error, mr: 1 }} />
                <Typography variant="body2" sx={{ color: COLORS.status.error }}>
                  Upload failed
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </UploadContainer>

      {displayError && (
        <Typography 
          variant="body2" 
          sx={{ 
            color: COLORS.status.error, 
            mt: 1,
            textAlign: 'center',
          }}
        >
          {displayError}
        </Typography>
      )}
    </Box>
  );
};

export default VideoUploadZone;